import { SiteHeader } from "@/components/site-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { CopyId } from "@/components/copy-id";
import {
  type ProfileCard,
  type UpdateCardPayload,
  cardsProvider,
} from "@/providers/cards.provider";
import {
  IconCards,
  IconChevronLeft,
  IconChevronRight,
  IconCheck,
  IconLoader2,
  IconPhoto,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUpload,
  IconUserMinus,
  IconUserPlus,
  IconX,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { toast } from "sonner";

const PAGE_SIZE = 50;

// ── Helpers ───────────────────────────────────────────────────────────────────

function ActiveBadge({ isActive }: { isActive: boolean }) {
  return isActive ? (
    <Badge variant="outline" className="border-green-500 text-green-600">
      Active
    </Badge>
  ) : (
    <Badge variant="secondary">Inactive</Badge>
  );
}

function CardRow({
  card,
  onClick,
}: {
  card: ProfileCard;
  onClick: () => void;
}) {
  const imgSrc = card.img_url ?? null;
  const createdAt = new Date(card.created_at).toLocaleDateString();

  return (
    <TableRow className="cursor-pointer" onClick={onClick}>
      <TableCell>
        <CopyId id={card.profile_card_id} />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-3">
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={card.name}
              className="size-8 rounded object-cover border"
            />
          ) : (
            <div className="size-8 rounded border bg-muted flex items-center justify-center">
              <IconPhoto className="size-4 text-muted-foreground" />
            </div>
          )}
          <span className="font-medium">{card.name}</span>
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground max-w-xs truncate">
        {card.description || "—"}
      </TableCell>
      <TableCell>
        <ActiveBadge isActive={card.is_active} />
      </TableCell>
      <TableCell className="text-muted-foreground text-xs">{createdAt}</TableCell>
    </TableRow>
  );
}

// ── Create card dialog ────────────────────────────────────────────────────────

function CreateCardSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState<{ file: File; base64: string } | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      cardsProvider.create({
        name: name.trim(),
        description: description.trim() || undefined,
        img_content: imageFile!.base64,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile-cards"] });
      toast.success("Card created");
      resetAndClose();
    },
    onError: (err) => toast.error(err.message),
  });

  function resetAndClose() {
    setName("");
    setDescription("");
    setImageFile(null);
    onClose();
  }

  function handleClose() {
    if (!isPending) resetAndClose();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Only JPEG and PNG images are supported");
      return;
    }
    if (file.size > 500 * 1024) {
      toast.error("Image must be under 500 KB");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(",")[1];
      setImageFile({ file, base64 });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && handleClose()}>
      <SheetContent className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>New Profile Card</SheetTitle>
          <SheetDescription>Create a new collectible card for users.</SheetDescription>
        </SheetHeader>
        <div className="px-4 flex flex-col gap-4">
          {/* Image picker */}
          <div className="flex flex-col gap-1.5">
            <Label>Image</Label>
            <div className="flex items-center gap-3">
              <div className="size-16 rounded-lg border bg-muted flex items-center justify-center overflow-hidden shrink-0 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => fileRef.current?.click()}>
                {imageFile ? (
                  <img
                    src={URL.createObjectURL(imageFile.file)}
                    alt="preview"
                    className="size-full object-cover"
                  />
                ) : (
                  <IconPhoto className="size-6 text-muted-foreground" />
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  className="gap-1.5"
                >
                  <IconUpload className="size-3.5" />
                  {imageFile ? "Change image" : "Choose image"}
                </Button>
                {imageFile ? (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-destructive text-left"
                    onClick={() => setImageFile(null)}
                  >
                    Remove
                  </button>
                ) : null}
                <p className="text-xs text-muted-foreground">JPEG or PNG, max 500 KB</p>
                <p className="text-xs text-muted-foreground">Recommended: 1215 × 766 px</p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-card-name">Name</Label>
            <Input
              id="new-card-name"
              placeholder="e.g. Founder"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-card-desc">Description</Label>
            <Textarea
              id="new-card-desc"
              placeholder="Short description…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <SheetFooter className="px-4">
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => mutate()}
            disabled={isPending || !name.trim() || !imageFile}
          >
            {isPending ? (
              <IconLoader2 className="size-4 animate-spin" />
            ) : (
              <IconPlus className="size-4" />
            )}
            Create
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ── Card detail sheet ─────────────────────────────────────────────────────────

function CardDetailSheet({
  card,
  onClose,
}: {
  card: ProfileCard | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editActive, setEditActive] = useState(false);
  const [grantUserId, setGrantUserId] = useState("");
  const [userLookupInput, setUserLookupInput] = useState("");
  const [userLookupId, setUserLookupId] = useState("");
  const [revokeUserId, setRevokeUserId] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Sync edit state when card changes
  const prevCardId = useRef<string | null>(null);
  if (card && card.profile_card_id !== prevCardId.current) {
    prevCardId.current = card.profile_card_id;
    setEditName(card.name);
    setEditDesc(card.description);
    setEditActive(card.is_active);
    setConfirmDelete(false);
    setGrantUserId("");
    setRevokeUserId("");
    setUserLookupInput("");
    setUserLookupId("");
  }

  const updateMut = useMutation({
    mutationFn: (payload: UpdateCardPayload) =>
      cardsProvider.update(card!.profile_card_id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile-cards"] });
      toast.success("Card updated");
    },
    onError: (err) => toast.error(err.message),
  });

  const uploadMut = useMutation({
    mutationFn: (base64: string) =>
      cardsProvider.uploadImage(card!.profile_card_id, base64),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile-cards"] });
      toast.success("Image uploaded");
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMut = useMutation({
    mutationFn: () => cardsProvider.delete(card!.profile_card_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile-cards"] });
      toast.success("Card deleted");
      onClose();
    },
    onError: (err) => toast.error(err.message),
  });

  const grantMut = useMutation({
    mutationFn: (userId: string) =>
      cardsProvider.grantToUser(card!.profile_card_id, userId),
    onSuccess: () => {
      toast.success("Card granted");
      setGrantUserId("");
    },
    onError: (err) => toast.error(err.message),
  });

  const revokeMut = useMutation({
    mutationFn: (userId: string) =>
      cardsProvider.revokeFromUser(card!.profile_card_id, userId),
    onSuccess: () => {
      toast.success("Card revoked");
      setRevokeUserId("");
    },
    onError: (err) => toast.error(err.message),
  });

  function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Only JPEG and PNG images are supported");
      return;
    }
    if (file.size > 500 * 1024) {
      toast.error("Image must be under 500 KB");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(",")[1];
      uploadMut.mutate(base64);
    };
    reader.readAsDataURL(file);
    // reset so same file can be re-selected
    e.target.value = "";
  }

  const {
    data: userCards,
    isLoading: userCardsLoading,
    isError: userCardsError,
  } = useQuery({
    queryKey: ["user-cards-lookup", userLookupId],
    queryFn: () => cardsProvider.getCardsForUser(userLookupId),
    enabled: !!userLookupId,
    staleTime: 30_000,
  });

  const ownsThisCard =
    userCards?.some((c) => c.profile_card_id === card?.profile_card_id) ?? false;

  const imgSrc = card?.img_url ?? null;
  const anyPending =
    updateMut.isPending || deleteMut.isPending || uploadMut.isPending;

  const isDirty =
    card &&
    (editName !== card.name ||
      editDesc !== card.description ||
      editActive !== card.is_active);

  return (
    <Sheet
      open={!!card}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent className="sm:max-w-md overflow-y-auto">
        {card && (
          <>
            <SheetHeader>
              <SheetTitle>{card.name}</SheetTitle>
              <SheetDescription className="font-mono text-xs">
                {card.profile_card_id}
              </SheetDescription>
            </SheetHeader>

            {/* Image */}
            <div className="px-4 flex flex-col gap-2">
              {imgSrc ? (
                <div
                  className="w-full rounded-lg border bg-muted overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => fileRef.current?.click()}
                >
                  <img
                    src={imgSrc}
                    alt={card.name}
                    className="w-full h-auto"
                  />
                </div>
              ) : (
                <div
                  className="w-full rounded-lg border bg-muted flex items-center justify-center py-10 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => fileRef.current?.click()}
                >
                  <IconPhoto className="size-10 text-muted-foreground" />
                </div>
              )}
              <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploadMut.isPending}
                    className="gap-1.5"
                  >
                    {uploadMut.isPending ? (
                      <IconLoader2 className="size-3.5 animate-spin" />
                    ) : (
                      <IconUpload className="size-3.5" />
                    )}
                    {imgSrc ? "Replace image" : "Upload image"}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    JPEG or PNG, max 500 KB
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Recommended: 1215 × 766 px
                  </p>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png"
                className="hidden"
                onChange={handleImageChange}
              />
              </div>
            </div>

            <Separator />

            {/* Edit metadata */}
            <div className="px-4 flex flex-col gap-4">
              <h3 className="font-semibold text-sm">Details</h3>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-card-name">Name</Label>
                <Input
                  id="edit-card-name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-card-desc">Description</Label>
                <Textarea
                  id="edit-card-desc"
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="edit-card-active">Active</Label>
                <Switch
                  id="edit-card-active"
                  checked={editActive}
                  onCheckedChange={setEditActive}
                />
              </div>
              {isDirty && (
                <Button
                  size="sm"
                  onClick={() =>
                    updateMut.mutate({
                      name: editName.trim(),
                      description: editDesc.trim(),
                      is_active: editActive,
                    })
                  }
                  disabled={updateMut.isPending || !editName.trim()}
                  className="self-end"
                >
                  {updateMut.isPending ? (
                    <IconLoader2 className="size-4 animate-spin" />
                  ) : null}
                  Save changes
                </Button>
              )}
            </div>

            <Separator />

            {/* User lookup */}
            <div className="px-4 flex flex-col gap-3">
              <h3 className="font-semibold text-sm">Look up user cards</h3>
              <div className="flex gap-2">
                <Input
                  placeholder="User ID"
                  value={userLookupInput}
                  onChange={(e) => setUserLookupInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && setUserLookupId(userLookupInput.trim())}
                  className="font-mono text-xs"
                />
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!userLookupInput.trim()}
                  onClick={() => setUserLookupId(userLookupInput.trim())}
                  className="shrink-0"
                >
                  <IconSearch className="size-3.5" />
                </Button>
              </div>

              {userLookupId && (
                <div className="flex flex-col gap-2">
                  {userCardsLoading ? (
                    <div className="flex flex-col gap-1.5">
                      {Array.from({ length: 2 }).map((_, i) => (
                        <Skeleton key={i} className="h-8 w-full" />
                      ))}
                    </div>
                  ) : userCardsError ? (
                    <p className="text-xs text-muted-foreground">Failed to load cards for this user.</p>
                  ) : userCards?.length === 0 ? (
                    <p className="text-xs text-muted-foreground">This user has no cards.</p>
                  ) : (
                    <>
                      <div className={`flex items-center gap-1.5 text-xs font-medium ${ownsThisCard ? "text-green-600" : "text-muted-foreground"}`}>
                        {ownsThisCard
                          ? <><IconCheck className="size-3.5" /> Owns this card</>
                          : <><IconX className="size-3.5" /> Does not own this card</>
                        }
                      </div>
                      <div className="flex flex-col divide-y rounded-md border text-xs">
                        {userCards?.map((c) => (
                          <div
                            key={c.profile_card_id}
                            className={`flex items-center gap-2 px-2 py-1.5 ${c.profile_card_id === card?.profile_card_id ? "bg-muted/60" : ""}`}
                          >
                            {c.img_url ? (
                              <img src={c.img_url} alt={c.name} className="size-5 rounded object-cover shrink-0" />
                            ) : (
                              <div className="size-5 rounded bg-muted border shrink-0" />
                            )}
                            <span className={c.profile_card_id === card?.profile_card_id ? "font-medium" : ""}>{c.name}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <Separator />

            {/* Grant */}
            <div className="px-4 flex flex-col gap-3">
              <h3 className="font-semibold text-sm">Grant to user</h3>
              <div className="flex gap-2">
                <Input
                  placeholder="User ID"
                  value={grantUserId}
                  onChange={(e) => setGrantUserId(e.target.value)}
                  className="font-mono text-xs"
                />
                <Button
                  variant="outline"
                  size="sm"
                  disabled={grantMut.isPending || !grantUserId.trim()}
                  onClick={() => grantMut.mutate(grantUserId.trim())}
                  className="shrink-0 gap-1.5"
                >
                  {grantMut.isPending ? (
                    <IconLoader2 className="size-3.5 animate-spin" />
                  ) : (
                    <IconUserPlus className="size-3.5" />
                  )}
                  Grant
                </Button>
              </div>
            </div>

            <Separator />

            {/* Revoke */}
            <div className="px-4 flex flex-col gap-3">
              <h3 className="font-semibold text-sm">Revoke from user</h3>
              <div className="flex gap-2">
                <Input
                  placeholder="User ID"
                  value={revokeUserId}
                  onChange={(e) => setRevokeUserId(e.target.value)}
                  className="font-mono text-xs"
                />
                <Button
                  variant="outline"
                  size="sm"
                  disabled={revokeMut.isPending || !revokeUserId.trim()}
                  onClick={() => revokeMut.mutate(revokeUserId.trim())}
                  className="shrink-0 gap-1.5"
                >
                  {revokeMut.isPending ? (
                    <IconLoader2 className="size-3.5 animate-spin" />
                  ) : (
                    <IconUserMinus className="size-3.5" />
                  )}
                  Revoke
                </Button>
              </div>
            </div>

            <Separator />

            {/* Delete */}
            <div className="px-4 flex flex-col gap-3">
              <h3 className="font-semibold text-sm text-destructive">
                Danger zone
              </h3>
              {confirmDelete ? (
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-muted-foreground">
                    This will delete the card and remove it from all users.
                    This cannot be undone.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={deleteMut.isPending}
                      onClick={() => deleteMut.mutate()}
                      className="gap-1.5"
                    >
                      {deleteMut.isPending ? (
                        <IconLoader2 className="size-3.5 animate-spin" />
                      ) : (
                        <IconTrash className="size-3.5" />
                      )}
                      Confirm delete
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={deleteMut.isPending}
                      onClick={() => setConfirmDelete(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive border-destructive/30 hover:bg-destructive/10 self-start gap-1.5"
                  onClick={() => setConfirmDelete(true)}
                  disabled={anyPending}
                >
                  <IconTrash className="size-3.5" />
                  Delete card
                </Button>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function UserCardsPage() {
  const [page, setPage] = useState(1);
  const [selectedCard, setSelectedCard] = useState<ProfileCard | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: cards, isLoading, isError } = useQuery({
    queryKey: ["profile-cards"],
    queryFn: () => cardsProvider.listAll(),
    staleTime: 60_000,
  });

  const paginated = cards?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) ?? [];
  const hasMore = (cards?.length ?? 0) > page * PAGE_SIZE;

  return (
    <div className="flex flex-col h-full">
      <SiteHeader title="Profile Cards" />

      <div className="flex flex-col gap-4 flex-1 p-4 lg:p-6">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-muted-foreground">
            <IconCards className="size-4" />
            <span className="text-sm">
              {isLoading
                ? "Loading…"
                : cards
                  ? `${cards.length} card${cards.length !== 1 ? "s" : ""}`
                  : ""}
            </span>
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1.5">
            <IconPlus className="size-4" />
            New card
          </Button>
        </div>

        {/* Table */}
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Card ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="py-12 text-center text-muted-foreground"
                  >
                    Failed to load cards. Check your connection and try again.
                  </TableCell>
                </TableRow>
              ) : !paginated.length ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="py-12 text-center text-muted-foreground"
                  >
                    No profile cards yet.
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((card) => (
                  <CardRow
                    key={card.profile_card_id}
                    card={card}
                    onClick={() => setSelectedCard(card)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {(page > 1 || hasMore) && (
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="gap-1"
            >
              <IconChevronLeft className="size-3.5" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasMore}
              onClick={() => setPage((p) => p + 1)}
              className="gap-1"
            >
              Next
              <IconChevronRight className="size-3.5" />
            </Button>
          </div>
        )}
      </div>

      <CreateCardSheet open={createOpen} onClose={() => setCreateOpen(false)} />
      <CardDetailSheet
        card={selectedCard}
        onClose={() => setSelectedCard(null)}
      />
    </div>
  );
}
